import React from 'react';
import { Modal, Form, Col, Button } from 'react-bootstrap';

import './NewStructureSetDialog.css';
import ModelSelect from '../common/ModelSelect';
import { PredictionModel } from '../../web-apis/contouring-options';
import ModalDialog from '../common/ModalDialog';

type OwnProps = {
    isVisible: boolean,
    isAutoContouringInProgress: boolean,
    onNewStructureCreation: (selectedNewStructureSetOption: NewStructureSetOption, contouringAction: string) => void,
    onClose: () => void,
}

type OwnState = {
    selectedNewStructureSetOption: NewStructureSetOption,
    selectedPredictionModel: PredictionModel | undefined,
    isModelOptionValid: boolean,
    isAutoContourOptionInProgressValid: boolean,
    isConfirmationDialogVisible: boolean,
}

export enum NewStructureSetOption {
    NoSelection, FromScratch, AutoContour
}

class NewStructureSetDialog extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);
        const defaultNewStructureSetOption = NewStructureSetOption.AutoContour;
        this.state = {
            selectedNewStructureSetOption: defaultNewStructureSetOption,
            selectedPredictionModel: undefined,
            isModelOptionValid: true,
            isAutoContourOptionInProgressValid: true,
            isConfirmationDialogVisible: false,
        };
    }

    resetValidation = () => {
        this.setState({
            isModelOptionValid: true,
            isAutoContourOptionInProgressValid: true,
        });
    }

    handleCreateNewStructureSetClick = () => {
        this.setState({ isConfirmationDialogVisible: true });
    }

    handleCreateNewStructureSetConfirmed = () => {
        // validate current options:
        const model = this.state.selectedPredictionModel;

        // validate that if auto-contouring is set, a model must be selected
        if (this.state.selectedNewStructureSetOption === NewStructureSetOption.AutoContour && !model) {
            this.setState({ isModelOptionValid: false });
            return;
        }

        // validate that if auto-contouring is already in-progress, we can't start another one
        if (this.props.isAutoContouringInProgress && this.state.selectedNewStructureSetOption === NewStructureSetOption.AutoContour) {
            this.setState({ isAutoContourOptionInProgressValid: false });
            return;
        }

        this.resetValidation();

        // proceed if everything looks good
        // delay the the activation a bit so the dialog has time to close before possible auto-contouring is enabled. this will avoid
        // a case where any "auto-contouring is already in-progress" warnings quickly flash on the dialog that's already closing
        const action = model ? model.name : "";
        setTimeout(() => { this.props.onNewStructureCreation(this.state.selectedNewStructureSetOption, action); }, 100);
        this.handleClose();
    }

    handleClose = () => {
        this.setState({ isConfirmationDialogVisible: false });
        this.props.onClose();
    }

    handleOptionChange = (selectedNewStructureSetOption: NewStructureSetOption) => {
        let removeModelValidation = {};
        if (selectedNewStructureSetOption !== NewStructureSetOption.AutoContour) {
            removeModelValidation = { isModelOptionValid: true, isAutoContourOptionInProgressValid: true };
        }
        this.setState({ selectedNewStructureSetOption, ...removeModelValidation });
    }

    handleModelChange = (model: PredictionModel | undefined) => {
        this.setState({ selectedPredictionModel: model, isModelOptionValid: true });
        this.selectAutoContouring();
    }

    selectAutoContouring = () => {
        this.setState({ selectedNewStructureSetOption: NewStructureSetOption.AutoContour });
    }

    render() {
        let modelSelectValidation = {};
        if (!this.state.isModelOptionValid) {
            modelSelectValidation = {
                control: (provided: any) => ({
                    ...provided,
                    borderColor: "#ee892c"
                }),
            }
        }

        let autoContouringInProgressWarningStyle = 'autocontour-in-progress';
        if (!this.state.isAutoContourOptionInProgressValid) { autoContouringInProgressWarningStyle += ' autocontour-not-available' }

        return (
            <>
                <ModalDialog
                    size="lg"
                    show={this.props.isVisible && !this.state.isConfirmationDialogVisible}
                    onHide={this.handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>New structure set</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="new-structure-set-radio-options">

                                <Form.Row>
                                    <Col lg={5}>
                                        <div>Select prediction model for segmentation</div>
                                    </Col>
                                    <Col className="option-right-side" lg={7}>
                                        <ModelSelect
                                            onChange={this.handleModelChange}
                                            onMenuOpen={this.selectAutoContouring}
                                            currentPredictionModel={this.state.selectedPredictionModel}
                                            styles={modelSelectValidation}
                                            isDisabled={this.props.isAutoContouringInProgress}
                                        />
                                        {(!this.state.isModelOptionValid) && (<span className="invalid-prediction-model">Please select a prediction model.</span>)}
                                        {(this.props.isAutoContouringInProgress) && (<span className={autoContouringInProgressWarningStyle}>Auto-contouring is already in progress. Please wait for the current one to finish.</span>)}
                                    </Col>

                                </Form.Row>
                            </Form.Group>
                        </Form>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-primary" onClick={this.handleCreateNewStructureSetClick} disabled={this.props.isAutoContouringInProgress}>Create structure set</Button>
                        <Button variant="outline-secondary" onClick={this.handleClose}>Cancel</Button>
                    </Modal.Footer>
                </ModalDialog>

                {this.state.selectedPredictionModel && (
                    <ModalDialog
                        show={this.state.isConfirmationDialogVisible}
                        backdrop="static"
                        size="lg"
                        >
                        <Modal.Header closeButton>
                            <Modal.Title>Confirm prediction model</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div>You are about to use the prediction model <b>{this.state.selectedPredictionModel.label}</b>. Do you wish to proceed?</div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" onClick={this.handleCreateNewStructureSetConfirmed}>Create structure set with {this.state.selectedPredictionModel.label}</Button>
                            <Button variant="outline-secondary" onClick={this.handleClose}>Cancel</Button>
                        </Modal.Footer>

                    </ModalDialog>
                )}
            </>
        );
    }
}

export default NewStructureSetDialog;
